/**
 * CreateSequenceDialog - Form-based dialog for creating Oracle sequences.
 *
 * Fields: Name, Schema, Start With, Increment By, Min/Max Value, Cache Size, Cycle, Order.
 * DDL preview panel shows the generated CREATE SEQUENCE statement.
 */

import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useOracleObjectDialogStore } from '../store/oracleObjectDialogStore';
import { generateCreateSequenceDDL } from '../utils/ddlGenerators';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export function CreateSequenceDialog() {
  const { openDialog, context, close } = useOracleObjectDialogStore();
  const isOpen = openDialog === 'create-sequence' && !!context;

  const [name, setName] = useState('');
  const [startWith, setStartWith] = useState('1');
  const [incrementBy, setIncrementBy] = useState('1');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [cacheSize, setCacheSize] = useState('20');
  const [cycle, setCycle] = useState(false);
  const [order, setOrder] = useState(false);

  const ddl = useMemo(() => {
    if (!context) return '';
    return generateCreateSequenceDDL({
      schema: context.schema,
      name: name || 'NEW_SEQUENCE',
      startWith,
      incrementBy,
      minValue,
      maxValue,
      cacheSize,
      cycle,
      order,
    });
  }, [context, name, startWith, incrementBy, minValue, maxValue, cacheSize, cycle, order]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      close();
    }
  };

  const resetForm = () => {
    setName('');
    setStartWith('1');
    setIncrementBy('1');
    setMinValue('');
    setMaxValue('');
    setCacheSize('20');
    setCycle(false);
    setOrder(false);
  };

  const handleCopyToEditor = () => {
    if (!context) return;
    openQueryTab(context.connectionId, context.database, ddl, `New Sequence`);
    resetForm();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px]" data-testid="create-sequence-dialog">
        <DialogHeader>
          <DialogTitle>New Sequence</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Form Fields */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="seq-schema">Schema</Label>
              <Input id="seq-schema" value={context?.schema ?? ''} disabled className="mt-1" />
            </div>
            <div>
              <Label htmlFor="seq-name">Name</Label>
              <Input id="seq-name" data-testid="seq-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="NEW_SEQUENCE" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="seq-start">Start With</Label>
                <Input id="seq-start" data-testid="seq-start" value={startWith} onChange={(e) => setStartWith(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="seq-incr">Increment By</Label>
                <Input id="seq-incr" data-testid="seq-incr" value={incrementBy} onChange={(e) => setIncrementBy(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="seq-min">Min Value</Label>
                <Input id="seq-min" value={minValue} onChange={(e) => setMinValue(e.target.value)} placeholder="(none)" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="seq-max">Max Value</Label>
                <Input id="seq-max" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} placeholder="(none)" className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="seq-cache">Cache Size</Label>
              <Input id="seq-cache" data-testid="seq-cache" value={cacheSize} onChange={(e) => setCacheSize(e.target.value)} className="mt-1" />
            </div>
            <div className="flex items-center gap-4 pt-1">
              <div className="flex items-center gap-2">
                <Switch id="seq-cycle" checked={cycle} onCheckedChange={setCycle} />
                <Label htmlFor="seq-cycle">Cycle</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="seq-order" checked={order} onCheckedChange={setOrder} />
                <Label htmlFor="seq-order">Order</Label>
              </div>
            </div>
          </div>

          {/* DDL Preview */}
          <div>
            <Label>DDL Preview</Label>
            <pre
              className="mt-1 p-3 bg-muted rounded-md text-xs font-mono whitespace-pre-wrap min-h-[200px] border border-border overflow-auto max-h-[300px]"
              data-testid="seq-ddl-preview"
            >
              {ddl}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={handleCopyToEditor} data-testid="seq-copy-to-editor">
            Copy to Query Editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function openQueryTab(connectionId: string, database: string, ddl: string, title: string) {
  const { activeConnection, connection } = getConnectionById(connectionId);
  if (!activeConnection || !connection) return;

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${title}-${queryNumber}`,
    type: 'query',
    content: { query: ddl },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
}
